import { useNavigate } from "react-router-dom";
import { Star, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "@/lib/api";
import { AllergenBadge } from "@/components/AllergenBadge";
import { useCart } from "@/context/CartContext";

export const MealCard = ({ meal, onBuildBox }) => {
  const navigate = useNavigate();
  const { addItem } = useCart();

  const isBox = meal.is_custom_box;

  const handleAdd = (e) => {
    e.stopPropagation();
    if (isBox) {
      onBuildBox ? onBuildBox() : navigate("/builder");
      return;
    }
    addItem(meal, 1);
    toast.success(`${meal.name} added to cart!`);
  };

  const openDetail = () => {
    if (isBox) {
      onBuildBox ? onBuildBox() : navigate("/builder");
    } else {
      navigate(`/meal/${meal.meal_id}`);
    }
  };

  return (
    <div
      data-testid={`meal-card-${meal.meal_id}`}
      onClick={openDetail}
      className="group cursor-pointer bg-white rounded-3xl border-2 border-emerald-100/80 shadow-[0_8px_25px_-5px_rgba(16,185,129,0.08)] hover:shadow-[0_14px_34px_-6px_rgba(16,185,129,0.22)] transition-all duration-200 hover:-translate-y-1 overflow-hidden flex flex-col"
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={meal.image_url}
          alt={meal.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {meal.is_meal_of_day && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-rose-400 text-white text-xs font-bold px-3 py-1 rounded-full shadow">
            <Sparkles size={12} /> Meal of the Day
          </span>
        )}
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-white/95 text-amber-600 text-xs font-bold px-2.5 py-1 rounded-full shadow">
          <Star size={13} className="fill-amber-400 text-amber-400" />
          {(meal.kid_rating || 0).toFixed(1)}
        </span>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-display font-semibold text-lg text-slate-800 leading-tight">
          {meal.name}
        </h3>
        <p className="text-sm text-slate-500 mt-1 line-clamp-2 flex-1">
          {meal.description}
        </p>

        {meal.allergens?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {meal.allergens.map((a) => (
              <AllergenBadge key={a} allergen={a} />
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-4">
          <span className="font-display font-bold text-xl text-emerald-600">
            {isBox ? "$9.00" : formatPrice(meal.price_cents)}
          </span>
          <button
            data-testid={`meal-card-add-${meal.meal_id}`}
            onClick={handleAdd}
            className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm rounded-2xl px-4 py-2 shadow-md shadow-emerald-200 transition-all active:scale-95"
          >
            <Plus size={16} strokeWidth={3} />
            {isBox ? "Build" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};
